import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Campaign as CallIcon, Search as SearchIcon, ContentCut as SplitIcon, Map as MapIcon, MyLocation as LocationIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CourierCallDialog from './CourierCallDialog';
import CourierSettlementDialog from './CourierSettlementDialog';
import CourierCashHandoverDialog from './CourierCashHandoverDialog';
import CourierMapDialog from './CourierMapDialog';
import { agoText, freshnessColor, locationInfo } from './courierLocation';
import FiyuuSendDialog from './FiyuuSendDialog';
import {
  isFiyuuCourier,
  CourierCall,
  CourierCollection,
  CourierDay,
  CourierPanelRow,
  cashOf,
  QuoteResult,
  courierModuleService,
  formatMoney,
  providerLabel,
  toApiSource,
} from './courierModuleService';

/** Panelden yapılacak tek atama (sipariş + kurye + parça). */
export interface PanelAssignItem {
  order: any;
  courier: CourierPanelRow;
  partNo: number;
  partCount: number;
  partNote?: string | null;
  collectsPayment: boolean;
  zoneId?: number | null;
  feeOverride?: number | null;
}

interface Props {
  open: boolean;
  /** "Kurye ata" ile açılınca atama, "Kuryeler" ile açılınca takip. */
  mode: 'assign' | 'view';
  branchId?: string | null;
  primaryOrder?: any | null;
  /** Birlikte atanabilecek diğer hazır siparişler. */
  candidateOrders?: any[];
  focusCourierId?: number | null;
  onClose: () => void;
  onAssign: (items: PanelAssignItem[]) => Promise<void>;
}

type Filter = 'all' | 'available' | 'on_road' | 'OWN' | 'FREELANCE' | 'COMPANY' | 'PLATFORM_VALET';

interface SplitPart {
  courierId: number | '';
  note: string;
}

const REFRESH_MS = 20000;
/** Açık kurye çağrısı varken cevaplar daha sık okunur. */
const CALL_REFRESH_MS = 5000;

const orderKey = (o: any) => `${toApiSource(o?.source)}:${o?.id}`;

const minutesAgo = (iso?: string | null): number | null => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso.endsWith('Z') ? iso : `${iso}Z`).getTime();
  return ms > 0 ? Math.round(ms / 60000) : 0;
};

const timeOf = (iso?: string | null) =>
  iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '—';

const CourierPanel: React.FC<Props> = ({
  open,
  mode,
  branchId,
  primaryOrder,
  candidateOrders = [],
  focusCourierId,
  onClose,
  onAssign,
}) => {
  const { t } = useTranslation('salesSystem');
  const assignMode = mode === 'assign' && !!primaryOrder;

  const [rows, setRows] = useState<CourierPanelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [day, setDay] = useState<CourierDay | null>(null);
  const [dayTab, setDayTab] = useState(0);
  const [collections, setCollections] = useState<CourierCollection[]>([]);
  const [retrying, setRetrying] = useState<number | null>(null);

  // Atama
  const [extraKeys, setExtraKeys] = useState<string[]>([]);
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [zoneOverrides, setZoneOverrides] = useState<Record<string, number>>({});
  const [learnZone, setLearnZone] = useState(true);
  const [feeOverride, setFeeOverride] = useState<string>('');
  const [split, setSplit] = useState(false);
  const [parts, setParts] = useState<SplitPart[]>([{ courierId: '', note: '' }, { courierId: '', note: '' }]);
  const [payerPart, setPayerPart] = useState(0);
  const [assigning, setAssigning] = useState(false);
  const [confirmUnassign, setConfirmUnassign] = useState<number | null>(null);

  // Kurye çağır
  const [calls, setCalls] = useState<CourierCall[]>([]);
  const [callDialog, setCallDialog] = useState<{ open: boolean; call: CourierCall | null; order: any | null }>({ open: false, call: null, order: null });
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [handoverFor, setHandoverFor] = useState<CourierPanelRow | null>(null);
  const [mapFor, setMapFor] = useState<{ open: boolean; courierId: number | null }>({ open: false, courierId: null });
  const callPickApplied = useRef(false);

  const ordersToAssign = useMemo(() => {
    if (!assignMode || !primaryOrder) return [];
    const extras = candidateOrders.filter((o) => extraKeys.includes(orderKey(o)));
    return [primaryOrder, ...extras];
  }, [assignMode, primaryOrder, candidateOrders, extraKeys]);

  const loadPanel = useCallback(async () => {
    setLoading((prev) => prev || rows.length === 0);
    try {
      const list = await courierModuleService.panel(branchId || undefined);
      setRows(list);
      setError(null);
    } catch (e: any) {
      setError(e?.message || t('courierModule.panel.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [branchId, rows.length, t]);

  const loadCalls = useCallback(async () => {
    try {
      setCalls(await courierModuleService.listCalls(branchId || undefined));
    } catch {
      // Çağrılar okunamazsa panel çalışmaya devam eder
    }
  }, [branchId]);

  const loadQuote = useCallback(async () => {
    if (!assignMode || !branchId || ordersToAssign.length === 0) {
      setQuote(null);
      return;
    }
    try {
      const q = await courierModuleService.quote(
        branchId,
        ordersToAssign.map((o) => ({
          source: toApiSource(o.source),
          orderRef: o.id,
          zoneId: zoneOverrides[orderKey(o)] ?? null,
        }))
      );
      setQuote(q);
    } catch {
      setQuote(null);
    }
  }, [assignMode, branchId, ordersToAssign, zoneOverrides]);

  const loadDay = useCallback(async (id: number) => {
    try {
      setDay(await courierModuleService.courierDay(id));
    } catch {
      setDay(null);
    }
    try {
      setCollections(await courierModuleService.collections({ branchId, courierId: id }));
    } catch {
      setCollections([]);
    }
  }, [branchId]);

  /** Faturalanamayan kurye ödemesini yeniden dener. */
  const retryCollection = async (id: number) => {
    setRetrying(id);
    setError(null);
    try {
      const r = await courierModuleService.retryCollection(id);
      if (r.status === 'failed') setError(r.message);
    } catch (e: any) {
      setError(e?.message || t('courierModule.panel.collections.retryFailed'));
    } finally {
      setRetrying(null);
      if (selectedId) void loadDay(selectedId);
      void loadPanel();
    }
  };

  // Açılış
  useEffect(() => {
    if (!open) return;
    setFilter('all');
    setSearch('');
    setExtraKeys([]);
    setZoneOverrides({});
    setFeeOverride('');
    setSplit(false);
    setParts([{ courierId: '', note: '' }, { courierId: '', note: '' }]);
    setPayerPart(0);
    setConfirmUnassign(null);
    setDay(null);
    setSelectedId(focusCourierId ?? null);
    setCalls([]);
    callPickApplied.current = false;
    void loadPanel();
    void loadCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branchId, focusCourierId]);

  // Sipariş seçimi değişince ücret tahmini
  useEffect(() => {
    if (open) void loadQuote();
  }, [open, loadQuote]);

  // Canlı yenileme
  useEffect(() => {
    if (!open) return;
    const timer = window.setInterval(() => {
      void loadPanel();
      void loadCalls();
      if (selectedId) void loadDay(selectedId);
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [open, loadPanel, loadCalls, loadDay, selectedId]);

  // Açık çağrı varken kurye cevapları birkaç saniyede bir okunur
  const hasOpenCall = calls.some((c) => c.status === 'open');
  useEffect(() => {
    if (!open || !hasOpenCall) return;
    const timer = window.setInterval(() => void loadCalls(), CALL_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [open, hasOpenCall, loadCalls]);

  // Bu siparişin çağrısı: "Geliyorum" diyen kurye ön atanır (seçili gelir)
  const primaryCall = useMemo(
    () => (assignMode && primaryOrder
      ? calls.find((c) => c.orderRef === Number(primaryOrder.id) && c.orderSource === toApiSource(primaryOrder.source))
      : undefined),
    [assignMode, primaryOrder, calls]
  );
  useEffect(() => {
    if (!open || callPickApplied.current) return;
    const id = primaryCall?.acceptedCourierId;
    if (id && rows.some((r) => r.id === id)) {
      callPickApplied.current = true;
      setSelectedId(id);
    }
  }, [open, primaryCall, rows]);

  // İlk açılışta önerilen kurye (sıradaki) seçilir
  useEffect(() => {
    if (!open || selectedId || rows.length === 0) return;
    const first = rows.find((r) => r.queueNo === 1) || rows[0];
    setSelectedId(first.id);
  }, [open, rows, selectedId]);

  useEffect(() => {
    if (open && selectedId) void loadDay(selectedId);
  }, [open, selectedId, loadDay]);

  // Bölünmüş pakette 1. parça seçili kuryeye gider
  useEffect(() => {
    if (split && selectedId) setParts((prev) => prev.map((p, i) => (i === 0 ? { ...p, courierId: selectedId } : p)));
  }, [split, selectedId]);

  const quoteFor = useCallback(
    (courierId: number) => quote?.couriers.find((c) => c.courierId === courierId),
    [quote]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr');
    return rows.filter((r) => {
      if (q && !(r.name || '').toLocaleLowerCase('tr').includes(q)) return false;
      if (filter === 'all') return true;
      if (filter === 'available' || filter === 'on_road') return r.state === filter;
      return r.kind === filter;
    });
  }, [rows, filter, search]);

  const counts = useMemo(
    () => ({
      available: rows.filter((r) => r.state === 'available').length,
      on_road: rows.filter((r) => r.state === 'on_road').length,
      cash: rows.reduce((s, r) => s + (r.collectsCash ? cashOf(r) : 0), 0),
      fee: rows.reduce((s, r) => s + r.todayFee, 0),
    }),
    [rows]
  );

  const selected = rows.find((r) => r.id === selectedId) || null;
  const selectedQuote = selected ? quoteFor(selected.id) : undefined;
  const primaryQuote = quote?.orders[0];
  const missingZoneOrders = (quote?.orders || []).filter((o) => o.zoneMissing && !zoneOverrides[`${o.source}:${o.orderRef}`]);

  const canAssign = (() => {
    if (!assignMode || assigning) return false;
    if (split) {
      return parts.every((p) => p.courierId !== '' && quoteFor(Number(p.courierId))?.eligible !== false)
        && new Set(parts.map((p) => p.courierId)).size === parts.length;
    }
    return !!selected && (selectedQuote?.eligible ?? selected.assignable);
  })();

  // Fiyuu: önce Fiyuu'ya gönderilir, kabul edilirse Fiyuu kuryesine atanır (Fiyuu reddederse hiçbir şey değişmez)
  const [fiyuuOrder, setFiyuuOrder] = useState<any | null>(null);
  const [fiyuuSent, setFiyuuSent] = useState<Record<string, string>>({});

  const assignToFiyuu = async (order: any, externalOrderId: string) => {
    if (!selected) return;
    setAssigning(true);
    setError(null);
    try {
      await onAssign([{ order, courier: selected, partNo: 1, partCount: 1, collectsPayment: true,
        zoneId: zoneOverrides[orderKey(order)] ?? null, feeOverride: null }]);
      await courierModuleService.fiyuuLink(externalOrderId).catch(() => undefined);
      setFiyuuSent((prev) => { const next = { ...prev }; delete next[orderKey(order)]; return next; });
      onClose();
    } catch (e: any) {
      setError(t('courierModule.fiyuu.assignFailed', { id: externalOrderId, message: e?.message || '' }));
    } finally {
      setAssigning(false);
    }
  };

  const doAssign = async () => {
    if (!canAssign || !primaryOrder) return;
    if (split && parts.some((p) => isFiyuuCourier(rows.find((r) => r.id === Number(p.courierId))))) {
      setError(t('courierModule.fiyuu.noSplit'));
      return;
    }
    if (!split && isFiyuuCourier(selected)) {
      if (ordersToAssign.length !== 1) {
        setError(t('courierModule.fiyuu.oneAtATime'));
        return;
      }
      const order = ordersToAssign[0];
      const already = fiyuuSent[orderKey(order)];
      if (already) await assignToFiyuu(order, already);
      else setFiyuuOrder(order);
      return;
    }
    setAssigning(true);
    setError(null);
    try {
      // Elle seçilen bölge: mahalle bölgeye eklenir, bir dahakine kendiliğinden bulunur
      if (learnZone && branchId) {
        for (const o of quote?.orders || []) {
          const zoneId = zoneOverrides[`${o.source}:${o.orderRef}`];
          if (zoneId && o.zoneMissing && o.neighborhood) {
            await courierModuleService.learnNeighborhood(branchId, zoneId, o.neighborhood).catch(() => undefined);
          }
        }
      }

      const fee = feeOverride.trim() === '' ? null : Number(feeOverride.replace(',', '.'));
      let items: PanelAssignItem[];
      if (split) {
        items = parts.map((p, i) => ({
          order: primaryOrder,
          courier: rows.find((r) => r.id === Number(p.courierId))!,
          partNo: i + 1,
          partCount: parts.length,
          partNote: p.note.trim() || null,
          collectsPayment: i === payerPart,
          zoneId: zoneOverrides[orderKey(primaryOrder)] ?? null,
          feeOverride: null,
        }));
      } else {
        items = ordersToAssign.map((o) => ({
          order: o,
          courier: selected!,
          partNo: 1,
          partCount: 1,
          collectsPayment: true,
          zoneId: zoneOverrides[orderKey(o)] ?? null,
          feeOverride: ordersToAssign.length === 1 && fee != null && Number.isFinite(fee) ? fee : null,
        }));
      }
      await onAssign(items);
      onClose();
    } catch (e: any) {
      setError(e?.message || t('courierModule.panel.assignFailed'));
    } finally {
      setAssigning(false);
    }
  };

  const unassign = async (activeOrderId: number) => {
    if (confirmUnassign !== activeOrderId) {
      setConfirmUnassign(activeOrderId);
      return;
    }
    setConfirmUnassign(null);
    try {
      await courierModuleService.unassign(activeOrderId);
      await loadPanel();
      if (selectedId) await loadDay(selectedId);
    } catch (e: any) {
      setError(e?.message || t('courierModule.panel.unassignFailed'));
    }
  };

  // ===================== Görünüm parçaları =====================

  const kindChip = (r: CourierPanelRow) => (
    <Chip
      size="small"
      sx={{ height: 20, fontSize: 11 }}
      color={r.kind === 'OWN' ? 'primary' : r.kind === 'PLATFORM_VALET' ? 'secondary' : 'default'}
      variant={r.kind === 'OWN' ? 'filled' : 'outlined'}
      label={r.kind === 'PLATFORM_VALET' ? providerLabel(r.providerCode) || t('courierModule.kinds.PLATFORM_VALET') : t(`courierModule.kinds.${r.kind}`)}
    />
  );

  const stateLine = (r: CourierPanelRow) => {
    if (!r.assignable) return t('courierModule.panel.notAssignable', { reason: r.notAssignableReason || '' });
    if (r.state === 'on_road') return t('courierModule.panel.onRoad', { count: r.inHandCount });
    const idle = minutesAgo(r.lastDeliveredAt);
    return idle != null ? t('courierModule.panel.idleFor', { minutes: idle }) : t('courierModule.panel.available');
  };

  /** Kuryenin açık bir çağrıya "Geliyorum" cevabı. */
  const comingFor = (courierId: number) => {
    for (const c of calls) {
      const a = c.answers.find((x) => x.courierId === courierId && x.answer === 'coming');
      if (a && (c.status === 'open' || c.acceptedCourierId === courierId)) return { call: c, answer: a };
    }
    return null;
  };

  const courierCard = (r: CourierPanelRow) => {
    const q = quoteFor(r.id);
    const coming = comingFor(r.id);
    const eligible = (q?.eligible ?? true) && r.assignable;
    const isSelected = r.id === selectedId;
    return (
      <Paper
        key={r.id}
        variant="outlined"
        onClick={() => setSelectedId(r.id)}
        sx={{
          p: 1,
          px: 1.25,
          display: 'grid',
          gridTemplateColumns: '36px minmax(0, 1fr) auto',
          gap: 1,
          alignItems: 'center',
          cursor: 'pointer',
          opacity: eligible ? 1 : 0.5,
          borderWidth: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : r.queueNo === 1 ? 'success.main' : 'divider',
          boxShadow: isSelected ? (theme) => `0 0 0 3px ${theme.palette.primary.light}33` : 'none',
        }}
      >
        <Avatar sx={{ width: 34, height: 34, fontSize: 14, bgcolor: r.kind === 'OWN' ? 'primary.main' : 'grey.600' }}>
          {(r.name || '?').charAt(0)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography fontWeight={700} fontSize={13} noWrap>
              {r.name}
            </Typography>
            {kindChip(r)}
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flex: 'none',
                bgcolor: !r.assignable ? 'grey.500' : r.state === 'on_road' ? 'warning.main' : 'success.main',
              }}
            />
            <Typography variant="caption" color="text.secondary" noWrap>
              {stateLine(r)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {t('courierModule.panel.todayLine', { count: r.todayDelivered, fee: formatMoney(r.todayFee) })}
            {r.collectsCash && cashOf(r) > 0 ? ` · ${t('courierModule.panel.cashLine', { amount: formatMoney(cashOf(r)) })}` : ''}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          {r.queueNo && <Chip size="small" color="success" sx={{ height: 20, fontSize: 11, mb: 0.25 }} label={t('courierModule.panel.queue', { no: r.queueNo })} />}
          {r.mappable && locationChip(r)}
          {r.pendingHandoverId && (
            <Chip
              size="small"
              color="warning"
              sx={{ height: 20, fontSize: 11, mb: 0.25, display: 'flex' }}
              label={t('courierModule.handover.cardChip', { amount: formatMoney(r.pendingHandoverAmount) })}
              onClick={(e) => { e.stopPropagation(); setSelectedId(r.id); setHandoverFor(r); }}
            />
          )}
          {r.failedCollections > 0 && (
            <Tooltip title={t('courierModule.panel.collections.failedHint')}>
              <Chip size="small" color="error" sx={{ height: 20, fontSize: 11, mb: 0.25, display: 'flex' }}
                label={t('courierModule.panel.collections.failedChip', { count: r.failedCollections })} />
            </Tooltip>
          )}
          {coming && (
            <Chip
              size="small"
              color="info"
              sx={{ height: 20, fontSize: 11, mb: 0.25, display: 'flex' }}
              label={t('courierModule.panel.calls.cardComing', {
                order: coming.call.orderNumber ? `#${coming.call.orderNumber}` : '',
                eta: coming.answer.etaMinutes ? ` ~${coming.answer.etaMinutes} dk` : '',
              })}
            />
          )}
          {assignMode && q && (
            eligible ? (
              <Typography fontWeight={800} fontSize={15}>
                {formatMoney(q.total)}
              </Typography>
            ) : (
              <Tooltip title={q.reason || ''}>
                <Typography variant="caption" color="text.secondary">
                  {t('courierModule.panel.notEligible')}
                </Typography>
              </Tooltip>
            )
          )}
        </Box>
      </Paper>
    );
  };

  /** Kurye kartındaki konum rozeti: yeşil < 3 dk · turuncu 3–10 dk · gri eski · kırmızı kapalı. Tıklayınca harita. */
  const locationChip = (r: CourierPanelRow) => {
    const info = locationInfo(r);
    const label = info.freshness === 'off'
      ? t('courierModule.map.off')
      : info.freshness === 'none'
        ? t('courierModule.map.none')
        : agoText(t, info.minutes, r.locationAt);
    return (
      <Tooltip title={t('courierModule.map.chipHint')}>
        <Chip
          size="small"
          color={freshnessColor(info.freshness)}
          variant={info.freshness === 'stale' || info.freshness === 'none' ? 'outlined' : 'filled'}
          sx={{ height: 20, fontSize: 11, mb: 0.25, display: 'flex' }}
          label={`📍 ${label}`}
          onClick={(e) => { e.stopPropagation(); setSelectedId(r.id); setMapFor({ open: true, courierId: r.id }); }}
        />
      </Tooltip>
    );
  };

  const kpi = (label: string, value: React.ReactNode, color?: string, onClick?: () => void, extra?: React.ReactNode) => (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1,
        px: 1.25,
        ...(onClick ? { cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } } : {}),
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography fontWeight={800} fontSize={18} color={color}>
        {value}
      </Typography>
      {extra}
    </Paper>
  );

  const paymentLabel = (p?: string | null) => t(`courierModule.payments.${(p || 'UNKNOWN').toUpperCase()}`, { defaultValue: p || '—' });

  const filterChips: { key: Filter; label: string }[] = [
    { key: 'all', label: `${t('courierModule.filters.all')} ${rows.length}` },
    { key: 'available', label: `${t('courierModule.panel.inShop')} ${counts.available}` },
    { key: 'on_road', label: `${t('courierModule.panel.onRoadShort')} ${counts.on_road}` },
    { key: 'OWN', label: t('courierModule.kinds.OWN') },
    { key: 'FREELANCE', label: t('courierModule.kinds.FREELANCE') },
    { key: 'COMPANY', label: t('courierModule.kinds.COMPANY') },
    { key: 'PLATFORM_VALET', label: t('courierModule.panel.valet') },
  ];

  const eligibleForSplit = rows.filter((r) => r.assignable && (quoteFor(r.id)?.eligible ?? true));

  return (
    <Dialog
      open={open}
      onClose={assigning ? undefined : onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: { xs: '100%', md: '88vh' }, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && assignMode && canAssign && !(e.target as HTMLElement)?.closest('input,textarea')) {
          e.preventDefault();
          void doAssign();
        }
      }}
    >
      {/* Üst çubuk */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
          {t('courierModule.panel.title')}
          <Chip size="small" label={t('courierModule.panel.live')} sx={{ ml: 1, bgcolor: 'common.white', color: 'success.main', fontWeight: 700 }} />
        </Typography>
        <TextField
          size="small"
          placeholder={t('courierModule.panel.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220, bgcolor: 'common.white', borderRadius: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<CallIcon />}
          sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,.5)' }}
          onClick={() => setCallDialog({
            open: true,
            call: primaryCall ?? null,
            order: assignMode ? primaryOrder : null,
          })}
        >
          {t('courierModule.panel.callCourier')}
        </Button>
        {rows.some((r) => r.mappable) && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<MapIcon />}
            sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,.5)' }}
            onClick={() => setMapFor({ open: true, courierId: null })}
          >
            {t('courierModule.map.allButton')}
          </Button>
        )}
        <IconButton onClick={onClose} sx={{ color: 'inherit' }} disabled={assigning}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Açık kurye çağrıları */}
      {calls.length > 0 && (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
          <CallIcon fontSize="small" color="primary" />
          {calls.map((c) => {
            const order = c.orderNumber ? `#${c.orderNumber}` : t('courierModule.panel.calls.noOrder');
            const declined = c.answers.filter((a) => a.answer === 'declined').length;
            const pending = c.answers.filter((a) => a.answer === 'pending').length;
            const accepted = c.answers.find((a) => a.courierId === c.acceptedCourierId);
            const label = c.status === 'taken'
              ? t('courierModule.panel.calls.stripTaken', {
                order,
                name: c.acceptedCourierName || '',
                eta: accepted?.etaMinutes ? ` ~${accepted.etaMinutes} dk` : '',
              })
              : t('courierModule.panel.calls.stripOpen', { order, pending, declined });
            return (
              <Chip
                key={c.id}
                size="small"
                color={c.status === 'taken' ? 'success' : 'default'}
                variant={c.status === 'taken' ? 'filled' : 'outlined'}
                label={label}
                onClick={() => setCallDialog({ open: true, call: c, order: null })}
              />
            );
          })}
        </Stack>
      )}

      {/* Bekleyen nakit teslim istekleri */}
      {rows.some((r) => r.pendingHandoverId) && (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'warning.50' }}>
          {rows.filter((r) => r.pendingHandoverId).map((r) => (
            <Chip
              key={r.id}
              size="small"
              color="warning"
              label={t('courierModule.handover.strip', { name: r.name || '', amount: formatMoney(r.pendingHandoverAmount), time: timeOf(r.pendingHandoverAt) })}
              onClick={() => { setSelectedId(r.id); setHandoverFor(r); }}
            />
          ))}
        </Stack>
      )}

      {/* Atanacak sipariş şeridi */}
      {assignMode && primaryOrder && (
        <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider', display: 'grid', gap: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) minmax(0, 1fr) auto' }, gap: 2, alignItems: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary">
                {t('courierModule.panel.toAssign')}
              </Typography>
              <Typography fontWeight={800} noWrap>
                #{primaryOrder.shortOrderNo || primaryOrder.id} · {primaryOrder.customerName}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {primaryQuote?.neighborhood || primaryOrder.customerAddress}
                {primaryQuote?.zoneName ? ` → ${t('courierModule.panel.zone')} ${primaryQuote.zoneName}` : ''}
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t('courierModule.panel.expectedPayment')}
              </Typography>
              <Typography fontWeight={800}>
                {paymentLabel(primaryQuote?.expectedPayment)} {formatMoney(primaryQuote?.amount ?? primaryOrder.totalAmount)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <FormControlLabel
                control={<Switch checked={split} onChange={(e) => setSplit(e.target.checked)} />}
                label={<Stack direction="row" spacing={0.5} alignItems="center"><SplitIcon fontSize="small" /><span>{t('courierModule.panel.split')}</span></Stack>}
              />
            </Stack>
          </Box>

          {/* Birlikte ata */}
          {!split && candidateOrders.length > 0 && (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary">
                {t('courierModule.panel.assignTogether')}
              </Typography>
              {candidateOrders.slice(0, 12).map((o) => {
                const k = orderKey(o);
                const on = extraKeys.includes(k);
                return (
                  <Chip
                    key={k}
                    size="small"
                    color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'}
                    label={`#${o.shortOrderNo || o.id} · ${o.customerName || ''}`}
                    onClick={() => setExtraKeys((prev) => (on ? prev.filter((x) => x !== k) : [...prev, k]))}
                  />
                );
              })}
            </Stack>
          )}

          {/* Bölge bulunamadı */}
          {missingZoneOrders.length > 0 && quote && quote.zones.length > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {missingZoneOrders.map((o) => (
                  <TextField
                    key={`${o.source}:${o.orderRef}`}
                    select
                    size="small"
                    label={t('courierModule.panel.pickZone', { order: o.orderNumber || o.orderRef, neighborhood: o.neighborhood || '?' })}
                    value={zoneOverrides[`${o.source}:${o.orderRef}`] ?? ''}
                    onChange={(e) => setZoneOverrides((prev) => ({ ...prev, [`${o.source}:${o.orderRef}`]: Number(e.target.value) }))}
                    sx={{ minWidth: 260 }}
                  >
                    {quote.zones.map((z) => (
                      <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
                    ))}
                  </TextField>
                ))}
                <FormControlLabel
                  control={<Checkbox size="small" checked={learnZone} onChange={(e) => setLearnZone(e.target.checked)} />}
                  label={t('courierModule.panel.rememberZone')}
                />
              </Stack>
            </Alert>
          )}

          {/* Paketi böl */}
          {split && (
            <Paper variant="outlined" sx={{ p: 1.25 }}>
              <Stack spacing={1}>
                {parts.map((p, i) => {
                  const q = p.courierId === '' ? undefined : quoteFor(Number(p.courierId));
                  return (
                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '70px 240px minmax(0,1fr) 150px 110px' }, gap: 1, alignItems: 'center' }}>
                      <Typography fontWeight={700}>{i + 1} / {parts.length}</Typography>
                      <TextField
                        select
                        size="small"
                        label={t('courierModule.panel.courier')}
                        value={p.courierId}
                        onChange={(e) => setParts((prev) => prev.map((x, j) => (j === i ? { ...x, courierId: Number(e.target.value) } : x)))}
                      >
                        {eligibleForSplit.map((r) => (
                          <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        label={t('courierModule.panel.partNote')}
                        placeholder={t('courierModule.panel.partNoteHint')}
                        value={p.note}
                        onChange={(e) => setParts((prev) => prev.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
                      />
                      <FormControlLabel
                        control={<Radio size="small" checked={payerPart === i} onChange={() => setPayerPart(i)} />}
                        label={t('courierModule.panel.collectsPayment')}
                      />
                      <Typography fontWeight={700} textAlign="right">{q ? formatMoney(q.fees[0] ?? 0) : '—'}</Typography>
                    </Box>
                  );
                })}
                <Stack direction="row" spacing={1}>
                  <Button size="small" disabled={parts.length >= 4} onClick={() => setParts((prev) => [...prev, { courierId: '', note: '' }])}>
                    {t('courierModule.panel.addPart')}
                  </Button>
                  <Button size="small" disabled={parts.length <= 2} onClick={() => setParts((prev) => prev.slice(0, -1))}>
                    {t('courierModule.panel.removePart')}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {t('courierModule.panel.splitNote')}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{error}</Alert>}

      {/* Gövde */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px minmax(0, 1fr)' } }}>
        <Box sx={{ borderRight: { md: 1 }, borderColor: 'divider', p: 1.5, overflowY: 'auto', bgcolor: 'background.default' }}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {filterChips.map((c) => (
              <Chip key={c.key} size="small" label={c.label} color={filter === c.key ? 'primary' : 'default'}
                variant={filter === c.key ? 'filled' : 'outlined'} onClick={() => setFilter(c.key)} />
            ))}
          </Stack>
          {loading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Alert severity="info">{t('courierModule.panel.empty')}</Alert>
          ) : (
            <Stack spacing={0.75}>{filtered.map(courierCard)}</Stack>
          )}
        </Box>

        <Box sx={{ p: 2, overflowY: 'auto', minWidth: 0 }}>
          {!selected ? (
            <Typography color="text.secondary">{t('courierModule.panel.pickCourier')}</Typography>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar sx={{ width: 44, height: 44, bgcolor: selected.kind === 'OWN' ? 'primary.main' : 'grey.600' }}>
                  {(selected.name || '?').charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" fontWeight={800} noWrap>{selected.name}</Typography>
                    {kindChip(selected)}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {[selected.phoneNumber, selected.parentCourierName, selected.agreementSummary || t('courierModule.list.noAgreement'),
                      selected.usesApp ? t('courierModule.panel.inApp') : t('courierModule.panel.noApp')].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {selected.mappable && (
                  <Button size="small" variant="outlined" startIcon={<LocationIcon />} onClick={() => setMapFor({ open: true, courierId: selected.id })}>
                    {t('courierModule.map.showButton')}
                  </Button>
                )}
                {selected.phoneNumber && (
                  <Button size="small" variant="outlined" href={`tel:${selected.phoneNumber}`}>{t('courierModule.panel.call')}</Button>
                )}
              </Stack>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 1 }}>
                {kpi(t('courierModule.panel.kpi.delivered'), day?.deliveredCount ?? selected.todayDelivered)}
                {kpi(t('courierModule.panel.kpi.inHand'), day?.inHand.length ?? selected.inHandCount)}
                {kpi(t('courierModule.panel.kpi.fee'), formatMoney(day?.feeTotal ?? selected.todayFee))}
                {kpi(
                  t('courierModule.panel.kpi.cash'),
                  selected.collectsCash ? formatMoney(cashOf(selected)) : '—',
                  'error.main',
                  selected.collectsCash ? () => setHandoverFor(selected) : undefined,
                  selected.collectsCash ? (
                    selected.pendingHandoverId ? (
                      <Chip size="small" color="warning" sx={{ height: 18, fontSize: 10.5 }}
                        label={t('courierModule.handover.kpiPending', { amount: formatMoney(selected.pendingHandoverAmount) })} />
                    ) : (
                      <Typography variant="caption" color="primary.main" fontWeight={700}>{t('courierModule.handover.kpiAction')}</Typography>
                    )
                  ) : undefined,
                )}
                {kpi(t('courierModule.panel.kpi.avg'), day?.averageDeliveryMinutes != null ? `${day.averageDeliveryMinutes} dk` : '—')}
                {kpi(t('courierModule.panel.kpi.month'), formatMoney(day?.monthFee ?? 0))}
              </Box>

              <Tabs value={dayTab} onChange={(_, v) => setDayTab(v)} variant="scrollable">
                <Tab label={`${t('courierModule.panel.tabs.inHand')} ${day?.inHand.length ?? 0}`} />
                <Tab label={`${t('courierModule.panel.tabs.delivered')} ${day?.deliveredCount ?? 0}`} />
                <Tab label={t('courierModule.panel.tabs.account')} />
              </Tabs>

              {dayTab === 0 && (
                (day?.inHand.length ?? 0) === 0 ? (
                  <Typography color="text.secondary">{t('courierModule.panel.noInHand')}</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('courierModule.panel.cols.order')}</TableCell>
                        <TableCell>{t('courierModule.panel.cols.customer')}</TableCell>
                        <TableCell>{t('courierModule.panel.cols.status')}</TableCell>
                        <TableCell>{t('courierModule.panel.cols.payment')}</TableCell>
                        <TableCell align="right">{t('courierModule.panel.cols.amount')}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {day!.inHand.map((o) => (
                        <TableRow key={o.activeOrderId}>
                          <TableCell>
                            {o.orderNumber}
                            {o.partCount > 1 && <Chip size="small" sx={{ ml: 0.5, height: 18 }} label={`${o.partNo}/${o.partCount}`} />}
                          </TableCell>
                          <TableCell>{o.customerName}</TableCell>
                          <TableCell>
                            {t(`courierModule.panel.status.${o.status}`, { defaultValue: o.status })} · {minutesAgo(o.pickedUpAt || o.assignedAt)} dk
                          </TableCell>
                          <TableCell>{o.paymentMethod}</TableCell>
                          <TableCell align="right">{formatMoney(o.totalAmount)}</TableCell>
                          <TableCell align="right">
                            <Button size="small" color={confirmUnassign === o.activeOrderId ? 'error' : 'inherit'} onClick={() => unassign(o.activeOrderId)}>
                              {confirmUnassign === o.activeOrderId ? t('courierModule.panel.confirmUnassign') : t('courierModule.panel.unassign')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              )}

              {dayTab === 1 && (
                (day?.deliveries.length ?? 0) === 0 ? (
                  <Typography color="text.secondary">{t('courierModule.panel.noDeliveries')}</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('courierModule.panel.cols.time')}</TableCell>
                        <TableCell>{t('courierModule.panel.cols.order')}</TableCell>
                        <TableCell>{t('courierModule.panel.cols.zone')}</TableCell>
                        <TableCell>{t('courierModule.panel.cols.payment')}</TableCell>
                        <TableCell align="right">{t('courierModule.panel.cols.amount')}</TableCell>
                        <TableCell align="right">{t('courierModule.panel.cols.fee')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {day!.deliveries.map((d) => (
                        <TableRow key={d.id} sx={d.status === 'cancelled' || d.zoneManual || d.feeManual ? { bgcolor: 'warning.light', '& td': { bgcolor: 'transparent' } } : undefined}>
                          <TableCell>{timeOf(d.deliveredAt || d.assignedAt)}</TableCell>
                          <TableCell>
                            {d.orderNumber || d.orderRef}
                            {d.partCount > 1 && <Chip size="small" sx={{ ml: 0.5, height: 18 }} label={`${d.partNo}/${d.partCount}`} />}
                          </TableCell>
                          <TableCell>{d.zoneName || d.neighborhood || '—'}{d.zoneManual ? ` · ${t('courierModule.panel.manual')}` : ''}</TableCell>
                          <TableCell>
                            {d.status === 'cancelled' ? t('courierModule.panel.status.cancelled') : paymentLabel(d.expectedPayment)}
                            {!d.collectsPayment && d.partCount > 1 ? ` · ${t('courierModule.panel.otherCollects')}` : ''}
                          </TableCell>
                          <TableCell align="right">{formatMoney(d.orderAmount)}</TableCell>
                          <Tooltip title={d.feeLines.map((l) => `${t(`courierModule.feeLines.${l.code}`, { defaultValue: l.label })}: ${formatMoney(l.amount)}`).join('\n')}>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatMoney(d.feeTotal)}{!d.feeFinal ? ' *' : ''}
                            </TableCell>
                          </Tooltip>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              )}

              {dayTab === 2 && day && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
                  {kpi(t('courierModule.panel.account.cash'), formatMoney(day.cashExpected))}
                  {kpi(t('courierModule.panel.account.card'), formatMoney(day.cardExpected))}
                  {kpi(t('courierModule.panel.account.online'), formatMoney(day.onlineTotal))}
                  {kpi(t('courierModule.panel.account.fee'), formatMoney(day.feeTotal))}
                  <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                    {t('courierModule.panel.account.note')}
                  </Typography>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {t('courierModule.panel.collections.title')}
                    </Typography>
                    {collections.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">{t('courierModule.panel.collections.empty')}</Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('courierModule.panel.cols.order')}</TableCell>
                            <TableCell>{t('courierModule.panel.cols.time')}</TableCell>
                            <TableCell>{t('courierModule.panel.cols.payment')}</TableCell>
                            <TableCell align="right">{t('courierModule.panel.collections.cash')}</TableCell>
                            <TableCell align="right">{t('courierModule.panel.collections.card')}</TableCell>
                            <TableCell>{t('courierModule.panel.cols.status')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {collections.map((c) => (
                            <TableRow key={c.id}>
                              <TableCell>#{c.orderNumber || c.orderRef}</TableCell>
                              <TableCell>{timeOf(c.createdAt)}</TableCell>
                              <TableCell>
                                {t(`courierModule.panel.collections.methods.${c.method}`)}
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t(`courierModule.panel.collections.origins.${c.origin}`)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{c.cashAmount > 0 ? formatMoney(c.cashAmount) : '—'}</TableCell>
                              <TableCell align="right">{c.cardAmount > 0 ? formatMoney(c.cardAmount) : '—'}</TableCell>
                              <TableCell>
                                <Tooltip title={c.error || c.invoiceNumber || ''}>
                                  <Chip
                                    size="small"
                                    color={c.status === 'processed' ? 'success' : c.status === 'failed' ? 'error' : 'warning'}
                                    variant={c.status === 'processed' ? 'outlined' : 'filled'}
                                    label={t(`courierModule.panel.collections.status.${c.status}`)}
                                  />
                                </Tooltip>
                                {(c.status === 'failed' || c.status === 'recorded') && (
                                  <Button size="small" sx={{ ml: 0.5 }} disabled={retrying === c.id} onClick={() => retryCollection(c.id)}>
                                    {retrying === c.id ? <CircularProgress size={14} /> : t('courierModule.panel.collections.retry')}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Alt çubuk */}
      {assignMode ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, flexWrap: 'wrap' }}>
          <Typography sx={{ flex: 1, minWidth: 240 }}>
            {split
              ? t('courierModule.panel.splitSummary', { count: parts.length })
              : selected
                ? t('courierModule.panel.assignSummary', {
                    name: selected.name,
                    orders: ordersToAssign.map((o) => `#${o.shortOrderNo || o.id}`).join(', '),
                    fee: formatMoney(selectedQuote?.total ?? 0),
                  })
                : t('courierModule.panel.pickCourier')}
          </Typography>
          {!split && ordersToAssign.length === 1 && (
            <TextField
              size="small"
              label={t('courierModule.panel.feeOverride')}
              value={feeOverride}
              onChange={(e) => setFeeOverride(e.target.value)}
              sx={{ width: 150 }}
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
            />
          )}
          <Button onClick={onClose} disabled={assigning}>{t('courierModule.actions.cancel')}</Button>
          <Button variant="contained" disabled={!canAssign} onClick={doAssign}
            startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {split
              ? t('courierModule.panel.assignSplit', { count: parts.length })
              : t('courierModule.panel.assignButton', { count: ordersToAssign.length })}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25 }}>
          <Typography sx={{ flex: 1 }}>
            {t('courierModule.panel.viewSummary', {
              available: counts.available,
              onRoad: counts.on_road,
              cash: formatMoney(counts.cash),
              fee: formatMoney(counts.fee),
            })}
          </Typography>
          <Button variant="outlined" onClick={() => setSettlementOpen(true)}>{t('courierModule.settlement.open')}</Button>
          <Button onClick={onClose}>{t('courierModule.actions.close')}</Button>
        </Box>
      )}
      <FiyuuSendDialog
        open={!!fiyuuOrder}
        branchId={branchId || ''}
        order={fiyuuOrder}
        onClose={() => setFiyuuOrder(null)}
        onSent={(externalOrderId) => {
          const order = fiyuuOrder;
          setFiyuuOrder(null);
          if (!order) return;
          setFiyuuSent((prev) => ({ ...prev, [orderKey(order)]: externalOrderId }));
          void assignToFiyuu(order, externalOrderId);
        }}
      />
      <CourierSettlementDialog
        open={settlementOpen}
        branchId={branchId}
        onClose={() => setSettlementOpen(false)}
        onChanged={() => void loadPanel()}
      />
      <CourierMapDialog
        open={mapFor.open}
        courierId={mapFor.courierId}
        branchId={branchId}
        onClose={() => { setMapFor({ open: false, courierId: null }); void loadPanel(); }}
      />
      <CourierCashHandoverDialog
        open={!!handoverFor}
        courierId={handoverFor?.id ?? null}
        courierName={handoverFor?.name}
        branchId={branchId}
        onClose={() => setHandoverFor(null)}
        onChanged={() => {
          void loadPanel();
          if (selectedId) void loadDay(selectedId);
        }}
      />
      <CourierCallDialog
        open={callDialog.open}
        branchId={branchId}
        order={callDialog.order}
        call={callDialog.call}
        rows={rows}
        onClose={() => setCallDialog({ open: false, call: null, order: null })}
        onChanged={() => void loadCalls()}
      />
    </Dialog>
  );
};

export default CourierPanel;
